'use client';

import { useAtomValue } from 'jotai';
import Image from 'next/image';

import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import Button from '@gds/Button/Button';
import { Typography } from '@gds/Typography/Typography';
import { useModal } from '@modal/Hooks/UseModal';
import { ctaClickedToEvent } from '@tracking/Schemas/CtaClicked/CtaClickedToEvent';
import { marketingToEvent } from '@tracking/Schemas/Marketing/MarketingToEvent';
import { userContextToEvent } from '@tracking/Schemas/UserContext/UserContextToEvent';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';

import autobizHero from './Images/hero.png';

import styles from '../../Autobiz/Autobiz.module.css';

interface AutobizBannerProps {
  autobizUrl?: string;
  dealerId?: string;
  strings: Record<string, any>;
  vehicleId: string;
}

const AutobizBanner = ({ autobizUrl, dealerId, strings, vehicleId }: AutobizBannerProps) => {
  const { openModal } = useModal();
  const userId = useAtomValue(userIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const formState = useAtomValue(formStateAtom);

  const handleOpenModal = () => {
    const ctaEventObj = {
      ctaType: 'button',
      ctaWording: 'Estimer ma voiture (Banner)',
      targetUrl: 'none',
    };
    const userData = {
      userId,
      values: { ...formState },
    };
    const marketingDataObj = {
      marketingValues: { ...(searchParams ?? {}) },
    };
    const userEvent = userContextToEvent(userData);
    const marketingEvent = marketingToEvent(marketingDataObj);

    const context = [userEvent, marketingEvent];

    trackCustomEvent({
      event: ctaClickedToEvent(ctaEventObj),
      context,
    });

    openModal(
      <iframe
        src={`${autobizUrl}/site/page-accueil?layout=iframe&vehicle_uid=${vehicleId}&dealer_uid=${dealerId}`}
        title={strings.header}
        width="100%"
      ></iframe>,
      {
        header: strings.header,
        modalClassName: styles.modalContainer,
        modalBodyClassName: styles.modalBody,
      },
    );
  };

  return (
    <article className={styles.autobizBanner}>
      <header>
        <Typography className={styles.title} variant={'h3'}>
          {strings.header}
        </Typography>
      </header>
      <div>
        <Image alt={strings.heroAlt} className={styles.image} src={autobizHero} />
        <p>{strings.copy}</p>
        <Button
          aria-label="autobiz-button"
          className={styles.button}
          data-test-id="autobiz-button"
          fullWidth
          onClick={handleOpenModal}
          type="submit"
          variant="outlined"
        >
          {strings.autobizCta}
        </Button>
      </div>
    </article>
  );
};

export default AutobizBanner;
